import { axiosInstance } from '../App';

export type DentitionType = 1 | 2 | 3;
export type FindingState = 1 | 2;
export type ToothSurface = 'V' | 'L' | 'M' | 'D' | 'O';

export interface OdontologyFinding {
  toothFDI: number;
  surface?: ToothSurface;
  typeCode: 'caries' | 'restoration' | 'crown' | 'endodontics' | 'bridge' | string;
  state: FindingState;
  icdas?: number;
}

export interface OdontologySpan {
  fromToothFDI: number;
  toToothFDI: number;
  typeCode: string;
  state: FindingState;
}

export interface OdontologyToothStatus {
  toothFDI: number;
  present: boolean;
  mobility?: number;
  furcation?: number;
  notes?: string;
}

export interface Odontogram {
  chartId?: number;
  version: number;
  dentitionType: DentitionType;
  savedAt?: string;
  findings: OdontologyFinding[];
  spans: OdontologySpan[];
  teeth: OdontologyToothStatus[];
}

export interface SaveOdontogram extends Odontogram {
  healthPatientServiceId?: number;
  label?: string;
}

export interface OdontogramSnapshotIndex {
  id: number;
  patientControlId: number;
  date: string;
  label?: string;
  findingCount: number;
  checksum: string;
}

const basePath = (patientId: number) =>
  `/api/secured/patients/${patientId}/odontology`;

export const getOdontogram = async (patientId: number): Promise<Odontogram> => {
  const response = await axiosInstance.get<Odontogram>(`${basePath(patientId)}/chart`);
  return response.data;
};

export const saveOdontogram = async (
  patientId: number,
  odontogram: SaveOdontogram,
): Promise<Odontogram> => {
  const response = await axiosInstance.put<Odontogram>(
    `${basePath(patientId)}/chart`,
    odontogram,
  );
  return response.data;
};

export const getOdontogramSnapshots = async (
  patientId: number,
): Promise<OdontogramSnapshotIndex[]> => {
  const response = await axiosInstance.get<OdontogramSnapshotIndex[]>(
    `${basePath(patientId)}/snapshots`,
  );
  return response.data;
};

export const getOdontogramSnapshot = async (
  patientId: number,
  snapshotId: number,
): Promise<Odontogram> => {
  const response = await axiosInstance.get<Odontogram>(
    `${basePath(patientId)}/snapshots/${snapshotId}`,
  );
  return response.data;
};

export const getOdontogramSnapshotByPatientControl = async (
  patientId: number,
  patientControlId: number,
): Promise<Odontogram> => {
  const response = await axiosInstance.get<Odontogram>(
    `${basePath(patientId)}/snapshots/by-control/${patientControlId}`,
  );
  return response.data;
};
